import colors from '@sportninja/common/constants/appColors'
import { Image } from 'antd'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import { updatePlayerRefreshments, updateOfficialRefreshments } from './utils'

interface ManageRefreshmentsModalContentProps {
  players: any[]
  gameId?: string
  rosterId?: string
  setIsManageRefreshmentsModalOpen: () => void
  readAttendanceInformation: () => void
}

const Container = styled.div`
  background-color: ${colors.SECONDARY_300};
  color: white;
  padding: 1rem;
  border-radius: 0.5rem;
  max-height: 500px;
  min-height: 500px;
  overflow-y: auto;
`

const Table = styled.table`
  width: 100%;
`

const TableHead = styled.thead`
  text-align: left;
  background-color: ${colors.SECONDARY_200};
  color: ${colors.NEUTRAL_50};
  font-size: 14px;
  line-height: 20px;

  & th {
    padding: 8px;

    &:first-child {
      border-top-left-radius: 8px;
    }
    &:last-child {
      border-top-right-radius: 8px;
    }
  }
`

const TableRow = styled.tr<{ clickable?: boolean }>`
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};

  &:nth-child(even) {
    background-color: ${colors.SECONDARY_200};
  }
`

const TableCell = styled.td`
  padding: 8px;
  overflow: hidden;
  color: ${colors.NEUTRAL_0};
  text-overflow: ellipsis;
  font-size: 16px;
  font-weight: 400;
  line-height: 23.2px;
  text-align: center;
  vertical-align: middle;
`

const PlayerImageWrapper = styled.div`
  width: 32px;
  height: 32px;
  margin-right: 8px;
  overflow: hidden;
  border-radius: 50%;
  background-color: ${colors.NEUTRAL_0};
`

const PlayerNameCell = styled.div`
  display: flex;
  align-items: center;
`

const PlayerPositionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const NumberCell = styled(TableCell)`
  text-align: center;
  border-width: 1px;
`

const playerImageStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
}

/**
 * Renders a table of players and team officials for refreshment assignment.
 * Clicking a row assigns refreshment duty to that player/official.
 */
const ManageRefreshmentsModalContent: React.FC<
  ManageRefreshmentsModalContentProps
> = ({
  players,
  gameId,
  rosterId,
  setIsManageRefreshmentsModalOpen,
  readAttendanceInformation,
}) => {
  const handleRowClick = useCallback(
    async (player: any) => {
      if (!gameId || !rosterId) {
        return
      }
      try {
        if (player?.player_type) {
          await updatePlayerRefreshments(gameId, rosterId, player.id)
        } else {
          await updateOfficialRefreshments(gameId, rosterId, player.id)
        }
        readAttendanceInformation()
        setIsManageRefreshmentsModalOpen()
      } catch (e) {
        console.error(e)
      }
    },
    [
      gameId,
      rosterId,
      readAttendanceInformation,
      setIsManageRefreshmentsModalOpen,
    ]
  )

  if (players.length === 0) {
    return <Container>No players available.</Container>
  }

  return (
    <Container>
      <Table>
        <TableHead>
          <tr>
            <th>#</th>
            <th>Player Name</th>
            <th>Position</th>
          </tr>
        </TableHead>
        <tbody>
          {players.map((player) => (
            <TableRow
              clickable
              onClick={() => handleRowClick(player)}
              key={player.id}
            >
              <NumberCell>{player?.player_number || '#'}</NumberCell>
              <TableCell>
                <PlayerNameCell>
                  <PlayerImageWrapper>
                    <Image
                      src={player.image}
                      alt={player?.full_name}
                      preview={false}
                      style={playerImageStyle}
                      fallback={
                        '/images/generic_placeholders/placeholder_player.png'
                      }
                    />
                  </PlayerImageWrapper>
                  {player?.name_first} {player?.name_last}
                </PlayerNameCell>
              </TableCell>
              <TableCell>
                <PlayerPositionContainer>
                  <div>{player?.player_type?.name_full || 'Team Official'}</div>
                </PlayerPositionContainer>
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>
    </Container>
  )
}

export default React.memo(ManageRefreshmentsModalContent)
